(() => {
  const allocationRows = (line) => Array.isArray(line.line_level_discount_allocations)
    ? line.line_level_discount_allocations
    : Array.isArray(line.discount_allocations) ? line.discount_allocations : [];
  const positiveAmount = (row) => Number(row.total_allocated_amount ?? row.amount ?? 0) > 0;
  const nativeAddError = async (response) => {
    const error = new Error('add');
    if (response.status === 422) {
      try {
        const body = await response.json();
        const message = [body.description,body.message].find(value => typeof value === 'string' && value.trim());
        if (message) error.storeMessage = message.trim().slice(0,600);
      } catch (_) { /* An unreadable error response keeps the generic cart-review message. */ }
    }
    return error;
  };
  const sameDiscount = (application, expected) => application
    && (expected.mode === 'automatic'
      // Dr Volume's native Ajax cart allocation returns an empty type; title, rate and amount are checked separately.
      ? ['automatic',''].includes(application.type) && application.title === expected.title
      : application.type === 'discount_code'
        && String(application.title || application.code || '').toLowerCase() === expected.title.toLowerCase());
  const cartApplications = (cart) => [
    ...(Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
    ...(Array.isArray(cart.discount_applications) ? cart.discount_applications : []),
    ...cart.items.flatMap(line => allocationRows(line).filter(row => Number(row.amount) > 0).map(row => row.discount_application))
  ].filter(Boolean);
  const stableValue = (value) => {
    if (Array.isArray(value)) return value.map(stableValue);
    if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map(key => [key,stableValue(value[key])]));
    return value;
  };
  const itemIdentity = (line) => JSON.stringify([
    String(line.variant_id),
    String(line.selling_plan_allocation?.selling_plan?.id || ''),
    stableValue(line.properties || {})
  ]);
  const itemQuantities = (items) => {
    const result = new Map();
    items.forEach(line => {
      const key = itemIdentity(line);
      result.set(key,(result.get(key) || 0) + line.quantity);
    });
    return result;
  };
  const assertCartShape = (cart, currency) => {
    if (!cart || !Array.isArray(cart.items) || cart.currency !== currency
      || cart.items.some(line => !line.variant_id || !Number.isSafeInteger(line.quantity) || line.quantity < 1)) throw new Error('cart-shape');
  };
  const assertPreflight = (cart, expected) => {
    assertCartShape(cart,expected.currency);
    if (cart.items.some(line => String(line.variant_id) === expected.variantId)) throw new Error('cart-conflict');
    assertDiscountContext(cart,expected);
  };
  const assertDiscountContext = (cart, expected) => {
    if (cartApplications(cart).some(application => !sameDiscount(application,expected))) throw new Error('cart-conflict');
    if (Array.isArray(cart.discount_codes) && cart.discount_codes.some(row => {
      const code = typeof row === 'string' ? row : row.code;
      return code && (expected.mode === 'automatic' || code.toLowerCase() !== expected.title.toLowerCase());
    })) throw new Error('cart-conflict');
  };
  const assertNativeCart = (before, cart, expected) => {
    assertCartShape(cart,expected.currency);
    assertDiscountContext(cart,expected);
    if (before.token && cart.token && before.token !== cart.token) throw new Error('cart-changed');
    const matching = cart.items.filter(line => line.properties?._bulk_request === expected.requestId);
    if (matching.length !== 1) throw new Error('line-count');
    const line = matching[0];
    const totalVariantQuantity = cart.items.filter(item => String(item.variant_id) === expected.variantId).reduce((sum,item) => sum + item.quantity,0);
    if (String(line.variant_id) !== expected.variantId || line.quantity !== expected.quantity
      || totalVariantQuantity !== expected.quantity || line.selling_plan_allocation) throw new Error('line-quantity');
    const beforeQuantities = itemQuantities(before.items);
    const afterQuantities = itemQuantities(cart.items.filter(item => item !== line));
    if (beforeQuantities.size !== afterQuantities.size
      || [...beforeQuantities].some(([key,value]) => afterQuantities.get(key) !== value)) throw new Error('cart-changed');
    if ((cart.cart_level_discount_applications || []).some(positiveAmount)) throw new Error('stacked-discount');
    if (line.original_price !== expected.base || line.original_line_price !== expected.base * expected.quantity
      || !Number.isSafeInteger(line.final_line_price) || line.final_line_price < 0
      || line.final_line_price >= line.original_line_price) throw new Error('native-price');
    const allocations = allocationRows(line).filter(row => Number(row.amount) !== 0);
    if (!allocations.length || allocations.some(row => !Number.isSafeInteger(row.amount) || row.amount < 0
      || !sameDiscount(row.discount_application,expected)
      || row.discount_application.target_type !== 'line_item'
      || row.discount_application.value_type !== 'percentage'
      || Number(row.discount_application.value) !== expected.percent)) throw new Error('discount-identity');
    const keys = new Set(allocations.map(row => row.discount_application.key).filter(Boolean));
    if (keys.size > 1) throw new Error('stacked-discount');
    const nativeDiscount = allocations.reduce((sum,row) => sum + row.amount,0);
    if (line.original_line_price - line.final_line_price !== nativeDiscount
      || (line.line_level_total_discount != null && line.line_level_total_discount !== nativeDiscount)) throw new Error('discount-total');
    const unitDiscount = expected.base * expected.percent / 100;
    if (nativeDiscount < Math.floor(unitDiscount) * expected.quantity
      || nativeDiscount > Math.ceil(unitDiscount) * expected.quantity) throw new Error('discount-rate');
    return line;
  };
  function boot(shop) {
    if (shop.dataset.ready) return;
    shop.dataset.ready = 'true';
    const root = shop.closest('[data-business-v2]');
    const products = [...shop.querySelectorAll('[data-bp3-product]')];
    const qty = shop.querySelector('#bp3-quantity');
    const review = shop.querySelector('[data-bp3-review]');
    const dialog = shop.querySelector('[data-bp3-dialog]');
    const add = shop.querySelector('[data-bp3-add]');
    const status = shop.querySelector('[data-bp3-status]');
    const dialogStatus = shop.querySelector('[data-bp3-dialog-status]');
    const cartLink = shop.querySelector('[data-bp3-cart]');
    const order = shop.querySelector('.bp3-order');
    const quoteLinks = [...root.querySelectorAll('[data-bp5-quote]')];
    const quoteLabels = new Map(quoteLinks.map(link => [link, link.textContent.trim()]));
    const tiers = JSON.parse(shop.querySelector('[data-bp3-tiers]').textContent);
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:shop.dataset.currency});
    let selected = null;
    let pending = false;
    let added = false;
    const addLabel = add.textContent.trim();
    const fmt = (cents) => money.format(cents/100);
    const qaPanel = shop.querySelector('[data-bp6-qa]');
    const qaEnabled = shop.dataset.qaDiagnostics === 'true'
      && Number(window.Shopify?.theme?.id) === 186922762455
      && ['development','unpublished'].includes(window.Shopify?.theme?.role);
    const readCart = async () => {
      const response=await fetch(shop.dataset.cartJsonUrl,{headers:{'Accept':'application/json'},credentials:'same-origin',cache:'no-store'});
      if(!response.ok)throw new Error('read-cart');
      return response.json();
    };
    const renderQaCart = (cart, status = 'Snapshot only; pricing has not been validated by this action.') => {
      if (!qaEnabled || !qaPanel) return;
      const application = (row = {}) => Object.fromEntries(['type','title','value_type','value','target_type','target_selection','allocation_method','total_allocated_amount'].filter(key => row[key] != null).map(key => [key,row[key]]));
      const snapshot = {status,currency:cart.currency,original_total_price:cart.original_total_price,total_price:cart.total_price,total_discount:cart.total_discount,
        cart_level_discount_applications:(cart.cart_level_discount_applications || []).map(application),
        discount_applications:(cart.discount_applications || []).map(application),
        discount_code_count:Array.isArray(cart.discount_codes) ? cart.discount_codes.length : 0,
        items:(cart.items || []).map(line => ({variant_id:line.variant_id,quantity:line.quantity,original_price:line.original_price,final_price:line.final_price,original_line_price:line.original_line_price,final_line_price:line.final_line_price,line_level_total_discount:line.line_level_total_discount,
          added_from_bulk_page:line.properties?._business_source === 'sothing-bulk-v8',
          allocations:allocationRows(line).map(row => ({amount:row.amount,discount_application:application(row.discount_application)}))}))};
      qaPanel.querySelector('[data-bp6-qa-output]').textContent = JSON.stringify(snapshot,null,2);
    };
    if (qaEnabled && qaPanel) {
      qaPanel.hidden=false;
      const readButton=qaPanel.querySelector('[data-bp6-qa-read]');
      readButton.addEventListener('click',async () => {
        readButton.disabled=true;
        try {renderQaCart(await readCart());}
        catch (_) {qaPanel.querySelector('[data-bp6-qa-output]').textContent='The native cart could not be read. No items were changed.';}
        finally {readButton.disabled=false;}
      });
    }
    const discountFor = (s) => {
      const mode = shop.dataset.discountMode || 'code';
      const title = String(mode === 'automatic' ? shop.dataset.automaticTitle || '' : s.tier?.code || '').trim();
      if (!['code','automatic'].includes(mode) || !title || !s.tier || (mode === 'code' && title.includes(','))) return null;
      return {mode,title,percent:s.tier.percent};
    };
    const canAdd = (s) => shop.dataset.checkoutEnabled === 'true' && shop.dataset.country === 'US'
      && selected?.dataset.available === 'true' && s.valid && discountFor(s);
    const state = () => {
      const quantity = Number(qty.value);
      const valid = Number.isSafeInteger(quantity) && quantity >= 3 && quantity <= 50;
      const tier = tiers.find((t) => quantity >= t.min && quantity <= t.max);
      const base = Number(selected?.dataset.price || 0);
      const unit = tier ? shop.dataset.discountMode === 'automatic'
        ? base - Math.floor(base*tier.percent/100)
        : Math.round(base*(100-tier.percent)/100) : base;
      return {quantity,valid,tier,base,unit,subtotal:base*quantity,total:unit*quantity};
    };
    const sync = () => {
      const s=state();
      status.textContent = !s.valid && selected ? (s.quantity > 50 ? shop.dataset.quoteOnly : shop.dataset.invalid) : '';
      qty.setAttribute('aria-invalid',String(!s.valid));
      review.disabled = !selected || !s.valid;
      shop.querySelector('[data-bp3-unit]').textContent = s.valid ? fmt(s.unit) : '—';
      shop.querySelector('[data-bp3-subtotal]').textContent = s.valid ? fmt(s.subtotal) : '—';
      shop.querySelector('[data-bp3-savings]').textContent = s.valid ? '−'+fmt(s.subtotal-s.total) : '—';
      shop.querySelector('[data-bp3-total]').textContent = s.valid ? fmt(s.total) : '—';
      shop.querySelector('[data-bp3-price-note]').textContent = s.tier ? s.tier.percent+'% '+shop.dataset.off : '';
      shop.querySelectorAll('[data-bp3-tier]').forEach((b) => b.setAttribute('aria-pressed',String(s.tier?.min === Number(b.dataset.bp3Tier))));
      products.forEach((card) => card.querySelectorAll('tbody tr').forEach((tr) => tr.classList.toggle('is-active',card === selected && s.quantity >= Number(tr.dataset.min) && s.quantity <= Number(tr.dataset.max))));
      quoteLinks.forEach(link => {
        link.textContent = selected && Number.isSafeInteger(s.quantity) && s.quantity > 50
          ? shop.dataset.quoteQuantity.replace('[quantity]',String(s.quantity))+' ↗'
          : quoteLabels.get(link);
      });
    };
    products.forEach((card) => card.querySelector('[data-bp3-select]')?.addEventListener('click',() => {
      selected = card;
      products.forEach((p) => {p.classList.toggle('is-selected',p===card);p.querySelector('[data-bp3-select]')?.setAttribute('aria-pressed',String(p===card));});
      shop.querySelector('[data-bp3-prompt]').hidden = true;
      shop.querySelector('[data-bp3-selection]').hidden = false;
      shop.querySelector('[data-bp3-model]').textContent = card.dataset.name;
      const thumb=shop.querySelector('[data-bp3-thumb]');thumb.src=card.dataset.image;thumb.alt=card.dataset.name;
      order.classList.add('has-selection');
      sync();
      if (window.matchMedia('(max-width:760px)').matches) order.scrollIntoView({behavior:window.matchMedia('(prefers-reduced-motion:reduce)').matches?'auto':'smooth',block:'start'});
    }));
    qty.addEventListener('input',sync);
    quoteLinks.forEach(link => link.addEventListener('click', () => {
      const structured = root.querySelector('[data-bp8-application]');
      if (structured && selected) {
        structured.querySelector('[name="product"]').value = selected.dataset.name;
        structured.querySelector('[name="quantity"]').value = String(state().quantity);
        structured.dispatchEvent(new CustomEvent('bp8:prefill'));
        return;
      }
      const form = root.querySelector('#bp-partner-form');
      if (!form && selected) {
        const email = root.querySelector('[data-bp-email-application]');
        if (email) {
          const enquiry = new URL(email.href);
          email.dataset.bpBaseBody ??= enquiry.searchParams.get('body') || '';
          enquiry.searchParams.set('body', email.dataset.bpBaseBody + '\n\nModel: ' + selected.dataset.name + '\nQuantity: ' + state().quantity);
          email.href = enquiry.href;
          email.focus({preventScroll:true});
        }
        return;
      }
      if (!form || !selected) return;
      const product = form.querySelector('#bp2-product_interest');
      const quantity = form.querySelector('#bp2-quantity');
      if (product && [...product.options].some(option => option.value === selected.dataset.product)) {
        product.value = selected.dataset.product;
        product.dispatchEvent(new Event('input',{bubbles:true}));
      }
      const copiedQuantity = quantity && Number.isSafeInteger(state().quantity) && state().quantity >= 1 && state().quantity <= 1000000;
      if (copiedQuantity) {
        quantity.value = String(state().quantity);
        quantity.dispatchEvent(new Event('input',{bubbles:true}));
      }
      form.dispatchEvent(new CustomEvent('bp2:prefill',{bubbles:true}));
      let notice = form.querySelector('[data-bp5-quote-notice]');
      if (!notice) {
        notice = document.createElement('p');
        notice.dataset.bp5QuoteNotice = '';
        notice.className = 'bp2-scope';
        notice.setAttribute('role','status');
        form.prepend(notice);
      }
      notice.textContent = (copiedQuantity ? shop.dataset.quoteReady : shop.dataset.quoteModel)+' '+selected.dataset.name+(copiedQuantity ? ' · '+quantity.value : '');
      const focusTarget = [...form.querySelectorAll('input:not([type="hidden"]),select,textarea,button')].find(el => !el.disabled && el.getClientRects().length);
      focusTarget?.focus({preventScroll:true});
    }));
    shop.querySelector('[data-bp3-minus]').addEventListener('click',() => {qty.value=Math.max(3,(Number(qty.value)||3)-1);sync();});
    shop.querySelector('[data-bp3-plus]').addEventListener('click',() => {qty.value=Math.min(1000000,(Number(qty.value)||3)+1);sync();});
    shop.querySelectorAll('[data-bp3-tier]').forEach((b) => b.addEventListener('click',() => {qty.value=b.dataset.bp3Tier;sync();}));
    review.addEventListener('click',() => {
      if (!selected || !state().valid) return;
      const target=shop.querySelector('[data-bp3-review-content]');target.replaceChildren();
      const model=shop.querySelector('.bp3-order-model').cloneNode(true);
      const quantity=document.createElement('p');quantity.textContent=shop.querySelector('label[for=bp3-quantity]').textContent+': '+state().quantity;
      const totals=shop.querySelector('.bp3-totals').cloneNode(true);
      target.append(model,quantity,totals);
      const enabled = canAdd(state());
      dialogStatus.textContent = added ? shop.dataset.error : enabled ? '' : shop.dataset.pending;
      add.disabled = !enabled || added;cartLink.hidden = !added;
      dialog.showModal();
    });
    shop.querySelectorAll('[data-bp3-close]').forEach((b) => b.addEventListener('click',() => {if(!pending) dialog.close();}));
    dialog.addEventListener('click',(e) => {if(e.target===dialog&&!pending){const r=dialog.getBoundingClientRect();if(e.clientX<r.left||e.clientX>r.right||e.clientY<r.top||e.clientY>r.bottom)dialog.close();}});
    dialog.addEventListener('cancel',(e) => {if(pending)e.preventDefault();});
    add.addEventListener('click',async () => {
      const s=state();
      if(pending||added||!canAdd(s))return;
      const expected={...discountFor(s),currency:shop.dataset.currency,variantId:String(selected.dataset.id),quantity:s.quantity,base:s.base,
        requestId:window.crypto?.randomUUID?.() || String(Date.now())+'-'+Math.random().toString(36).slice(2)};
      if(!/^\d+$/.test(expected.variantId)||!Number.isSafeInteger(Number(expected.variantId)))return;
      pending=true;add.disabled=true;add.setAttribute('aria-busy','true');add.firstChild.textContent=shop.dataset.adding;
      dialogStatus.textContent='';
      let latestCart;
      try {
        const before=await readCart();
        assertPreflight(before,expected);
        added=true;
        const response=await fetch(shop.dataset.addUrl,{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},credentials:'same-origin',body:JSON.stringify({items:[{id:Number(expected.variantId),quantity:expected.quantity,properties:{'_business_source':'sothing-bulk-v8','_business_program':'bulk','_commission_eligible':'no','_bulk_tier':String(s.tier.min)+'-'+String(s.tier.max),'_bulk_request':expected.requestId}}]})});
        if(!response.ok)throw await nativeAddError(response);
        if(expected.mode==='code'){
          const applied=await fetch(shop.dataset.updateUrl,{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},credentials:'same-origin',body:JSON.stringify({discount:expected.title})});
          if(!applied.ok)throw new Error('discount');
        }
        const cart=await readCart();
        latestCart=cart;
        const line=assertNativeCart(before,cart,expected);
        shop.querySelector('[data-bp3-total]').textContent=fmt(line.final_line_price);
        location.assign(shop.dataset.cartUrl);
      } catch (error) {
        if(latestCart)renderQaCart(latestCart,'Bulk validation failed: '+error.message);
        const failureMessage=added ? shop.dataset.error : error.message==='cart-conflict' ? shop.dataset.cartConflict : shop.dataset.preflightFailed;
        dialogStatus.textContent=[error.storeMessage,failureMessage].filter(Boolean).join(' ');
        cartLink.hidden=false;
      } finally {
        pending=false;add.disabled=added;add.removeAttribute('aria-busy');add.firstChild.textContent=addLabel.replace(/[↗→]$/,'').trim();
      }
    });
    if('IntersectionObserver' in window)new IntersectionObserver((entries)=>order.classList.toggle('is-visible',entries[0].isIntersecting),{threshold:0}).observe(order);
    if(root)root.classList.add('bp3-shop-ready');
  }
  const init=(scope)=>scope.querySelectorAll('[data-bp3-shop]').forEach(boot);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init(document));else init(document);
  document.addEventListener('shopify:section:load',(e)=>init(e.target));
})();
